import React, { FC, useContext, useState } from "react";
import { IconButton } from "@mui/material";
import { FiBox, FiDownloadCloud, FiEdit, FiPlus, FiTrash } from "react-icons/fi";
import { api, IdentityProviderStrategy, IListIdentityProvidersInput, IListIdentityProvidersOutput } from "../../../logic/api";
import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import { IdentityProviderForm } from "./Form";

import flex from "../../../styles/flex";
import classes from "./IdentityProviders.module.css";
import ConfirmationDialog from "../../../components/notifications/confirmationDialog/ConfirmationDialog";
import { SessionContext } from "../../../utility/useSession";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

interface IdentityProvidersPageProps { }

export const IdentityProvidersPage: FC<IdentityProvidersPageProps> = () => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [listParams, setListParams] = useState<IListIdentityProvidersInput>({});
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedIdentityProviderId, setSelectedIdentityProviderId] = useState<number | null>(null);

  const deleteIdP = api.hooks.admin.deleteIdentityProvider();

  const onEdit = (id: number) => {
    setSelectedIdentityProviderId(id);
    setShowForm(true);
  }

  const onDelete = (id: number) => {
    setSelectedIdentityProviderId(id);
    setShowDeleteConfirm(true);
  }

  const handleDeleteConfirmed = (confirmed: boolean) => {
    if (!confirmed || !selectedIdentityProviderId) {
      return;
    }

    const handleResult = () => {
      setShowDeleteConfirm(false);
      setListParams({});
    }

    deleteIdP.send(
      session.current.context,
      { id: selectedIdentityProviderId },
      handleResult,
    );
  };

  const onFormClose = () => {
    setShowForm(false);
    setSelectedIdentityProviderId(null);
  }

  const onFormSave = () => {
    setShowForm(false);
    setSelectedIdentityProviderId(null);
    setListParams({});
  }

  return (
    <>
      <div>
        <div style={{ ...flex.flexRows, marginBottom: 20 }}>
          <div style={flex.flexFill}>
            <IconButton
              href="/admin/identityProviders/getSpMetadata"
              color="secondary"
              title={"Download Service Provider Metadata XML File"}
            >
              <FiDownloadCloud />
            </IconButton>

          </div>
          <div style={flex.flexAuto}>
            <IconButton
              title="New Identity Provider"
              onClick={() => { setShowForm(true) }}
            >
              <FiPlus />
            </IconButton>
          </div>
        </div>

        <PagedDataView<IListIdentityProvidersInput, IListIdentityProvidersOutput>
          apiHook={api.hooks.admin.listIdentityProviders}
          parameters={listParams}
          renderItem={(identityProvider) => {
            return <ListItem key={identityProvider.id} identityProvider={identityProvider} onEdit={onEdit} onDelete={onDelete} />;
          }}
        >
          No identity providers found
        </PagedDataView>
      </div>
      <IdentityProviderForm open={showForm} identityProviderId={selectedIdentityProviderId} onClose={onFormClose} onSave={onFormSave} />
      <ConfirmationDialog
        title={t("admin.identityProviders.deleteConfirmation.title")}
        message={t("admin.identityProviders.deleteConfirmation.message")}
        show={showDeleteConfirm}
        onClose={handleDeleteConfirmed}
      />
    </>
  );
};

interface ListItemProps {
  identityProvider: IListIdentityProvidersOutput;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

const ListItem: FC<ListItemProps> = ({
  identityProvider,
  onEdit,
  onDelete,
}) => {
  return (
    <div className={classes.listItem}>
      <div className={classes.listIcon}>
        <FiBox size="24px" />
      </div>
      <div className={classes.listItemColumn}>
        <div>
          <div><strong>{identityProvider.name}</strong></div>
          <div>{identityProvider.issuer}</div>
        </div>
      </div>
      <div className={classes.listItemColumn}>
        <div>
          <div><strong>{IdentityProviderStrategy[identityProvider.strategy]}</strong></div>
        </div>
      </div>
      <div className={classes.listAction}>
        <IconButton onClick={() => onEdit(identityProvider.id)}>
          <FiEdit />
        </IconButton>

        <div>
          <small>{t("general.edit")}</small>
        </div>
      </div>
      <div className={classes.listAction}>
        <IconButton onClick={() => onDelete(identityProvider.id)}>
          <FiTrash />
        </IconButton>

        <div>
          <small>{t("general.delete")}</small>
        </div>
      </div>
    </div>
  );
}
